/**
 * Inicialización temprana de lazy loading para librerías pesadas
 * Este script intercepta accesos a window.pdfjsLib, window.XLSX, window.mammoth
 * y carga las librerías automáticamente cuando se detecta que se van a usar
 */

import { loadPDFjs, loadXLSX, loadMammoth } from './lazy-libraries.js';

/**
 * Helper para asegurar que una librería esté cargada antes de usarla
 * Útil para código que verifica `if (window.XLSX) { ... }`
 */
export async function ensureLibraryLoaded(libraryName) {
  switch (libraryName) {
    case 'XLSX':
      if (typeof window.XLSX === 'undefined') {
        await loadXLSX();
      }
      return window.XLSX;
    case 'pdfjsLib':
      if (typeof window.pdfjsLib === 'undefined') {
        await loadPDFjs();
      }
      return window.pdfjsLib;
    case 'mammoth':
      if (typeof window.mammoth === 'undefined') {
        await loadMammoth();
      }
      return window.mammoth;
    default:
      throw new Error(`Unknown library: ${libraryName}`);
  }
}

/**
 * Pre-carga las librerías en background cuando se detecta que se van a usar
 * Esto mejora la experiencia cuando el usuario hace clic en funciones que las necesitan
 */
export function preloadLibrariesOnInteraction() {
  // Pre-cargar cuando el usuario interactúa con elementos que probablemente las usen
  const preloadTriggers = [
    // Botones de exportar
    { selector: '[data-action*="export"], [data-action*="excel"], button[title*="Excel"], button[title*="Export"]', lib: 'XLSX' },
    // Inputs de archivo que aceptan PDF
    { selector: 'input[type="file"][accept*="pdf"], input[type="file"][accept*="application/pdf"]', lib: 'pdfjsLib' },
    // Inputs de archivo que aceptan Word
    { selector: 'input[type="file"][accept*="docx"], input[type="file"][accept*="application/vnd.openxmlformats-officedocument.wordprocessingml.document"]', lib: 'mammoth' }
  ];

  preloadTriggers.forEach(({ selector, lib }) => {
    document.addEventListener('mouseenter', (e) => {
      if (e.target.matches && e.target.matches(selector)) {
        // Pre-cargar en background sin bloquear
        ensureLibraryLoaded(lib).catch(() => {
          // Ignorar errores silenciosamente
        });
      }
    }, { passive: true, once: false });
  });
}

/**
 * Inicializa el sistema de lazy loading
 */
export function initLazyLibraries() {
  // Solo inicializar una vez
  if (window._lazyLibrariesInitialized) {
    return;
  }
  window._lazyLibrariesInitialized = true;

  // Pre-cargar en background cuando se detecta interacción
  if (typeof document !== 'undefined') {
    // Esperar a que el DOM esté listo
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        preloadLibrariesOnInteraction();
      });
    } else {
      preloadLibrariesOnInteraction();
    }
  }
}

// Auto-inicializar cuando se importa el módulo
if (typeof window !== 'undefined') {
  initLazyLibraries();
}

